import { useState, useEffect, useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import _ from "lodash";

import { baseUnitCreate } from "./createOrderSale.constants";

import classes from "./createOrderSale.module.scss";
import { ReactComponent as AddIcon } from "@/assets/images/desktop/agregar.svg";

import CreateOrderSaleTableItem from "./CreateOrderSaleTableItem";
import { formatMiles, formatMilesDecimalNew } from "@/services/Utils";
import { useOrderSaleCreate } from "@/hooks/purchases/orders_sale";
import Fetching from "@/components/Fetching/Fetching";
import CustomInfoMessage from "@/components/desktop/list/CustomInfoMessage";

const CreateOrderSaleTableDetail = ({ params, data, setData, isFetchingDetail, alerts, setAlerts, handleValidPrice }) => {
	const { addToast } = useToasts();

	const { data: response } = useOrderSaleCreate(params);
	const { units_measurement } = response;

	const [activeProduct, setActiveProduct] = useState(true);
	const [products, setProducts] = useState([]);
	const [products_generic, setProductsGeneric] = useState([]);
	const [reload, setReload] = useState(true);

	const [net, setNet] = useState(0);
	const [quantityContainers, setQuantityContainers] = useState(0);

	useEffect(() => {
		if (!response) return;

		let newProducts = [...(response.products || [])];
		let newProductsGeneric = [...(response.products_generic || [])];

		data.items.forEach((item) => {
			if (item.product_id && !newProducts.find((p) => p.value === item.product_id)) {
				newProducts.push({
					label: item.product_id,
					value: item.product_id,
					unit_measurement: item.unit_measurement,
				});
			}

			if (item.save_as && !newProductsGeneric.find((p) => String(p.value) === String(item.save_as))) {
				newProductsGeneric.push({
					label: item.save_as,
					value: item.save_as,
					unit_measurement: item.unit_measurement,
					estate_id: data.estate_id,
				});
			}
		});

		setProducts(newProducts);
		setProductsGeneric(newProductsGeneric);
		// eslint-disable-next-line
	}, [response, data.estate_id]);

	useEffect(() => {
		if (!reload || !data.items.length) return;

		const newItems = data.items.map((item) => {
			let units = baseUnitCreate;
			const unitMeasurement = item.unit_measurement === "c" ? "cc" : item.unit_measurement;

			const itemUnit = units_measurement?.filter((um) => um.base_unit === unitMeasurement);
			if (itemUnit?.length) {
				units = itemUnit;
			}

			return {
				...item,
				unit_measurement: unitMeasurement,
				units_enabled: units,
				value_unit_measurement: item.value_unit_measurement || 1000,
			};
		});

		setData((prevData) => ({ ...prevData, items: newItems }));
		setReload(false);
		// eslint-disable-next-line
	}, [reload, data.items.length, units_measurement]);

	useEffect(() => {
		setQuantityContainers(_.sumBy(data.items, (item) => Number(item.quantity)));

		const newNet = _.sumBy(data.items, (item) => Number(String(item.price_total).replaceAll(",", ".")));
		setNet(String(newNet).replaceAll(".", ","));

		if (data.items.length === 0 && products.length > 0) {
			handleAddItem();
		}
		// eslint-disable-next-line
	}, [data.items, products.length]);

	const handleAddItem = () => {
		let newItems = [...data.items];
		newItems.push({
			hash: new Date().getTime(),
			id: "",
			application_history: "",
			billing_date: "",
			business_id: "",
			container_content: "",
			dealer: "",
			dealer_id: "",
			dealer_number: "",
			delivery_number: "",
			estate_id: "",
			expiration_date: "",
			inventory_id: "",
			lot: "",
			price: "",
			price_total: "",
			product_id: "",
			quantity: "",
			quantity_lot: "",
			save_as: "",
			tradename: "",
			type_currency: "",
			type_product: "",
			unit_measurement: "",
			value_unit_measurement: "",
			text_product: "",
			order_detail_id: "",
			price_unit: "",
			units_enabled: [],
			suggestions: [],
			lot_production: "",
		});
		setData({ ...data, items: newItems });
	};

	const delayedQuery = useMemo(
		() =>
			_.debounce((newItems, i, type_currency_id) => {
				handleValidPrice(newItems, i, type_currency_id);
			}, 500),
		[handleValidPrice],
	);

	useEffect(() => {
		return () => {
			delayedQuery.cancel();
		};
	}, [delayedQuery]);

	const changeProduct = (i, opt, _meta) => {
		let value = "";
		let unit_measurement = "";
		let value_unit_measurement = "";
		let newItems = [...data.items];

		if (opt) {
			value = opt.value;

			const product = products.find((p) => Number(p.value) === Number(value));

			if (product) {
				unit_measurement = product.unit_measurement;
				if (unit_measurement === "u") {
					value_unit_measurement = 1;
				}
			} else if (!products.find((p) => p.value === value)) {
				const newProduct = {
					label: value,
					value: value,
					unit_measurement: "",
				};

				let newProducts = [...products];
				newProducts.push(newProduct);

				setProducts(newProducts);
			}
		}

		newItems[i].unit_measurement = unit_measurement;
		newItems[i].value_unit_measurement = value_unit_measurement;
		newItems[i].product_id = value;

		setData({ ...data, items: newItems });
		setReload(true);
	};

	const changeTextProduct = (i, value) => {
		let newItems = [...data.items];
		newItems[i].text_product = value;
		setData({ ...data, items: newItems });
	};

	const changeCas = (i, value) => {
		let newItems = [...data.items];
		newItems[i].cas = value;
		setData({ ...data, items: newItems });
	};

	const changeQuantity = (i, value) => {
		value = String(value).replaceAll(".", "");

		let newItems = [...data.items];
		newItems[i].quantity = value;
		newItems[i].price_unit = newItems[i].price_total / value;

		if (newItems[i].unit_measurement === "u") {
			newItems[i].container_content = 1;
		}

		delayedQuery(newItems, i, data.type_currency_id);
		setData({ ...data, items: newItems });
	};

	const changeContainerContent = (i, value) => {
		value = String(value).replaceAll(".", "");

		let newItems = [...data.items];
		newItems[i].container_content = value;

		delayedQuery(newItems, i, data.type_currency_id);
		setData({ ...data, items: newItems });
	};

	const changeUnitMeasurement = (i, value) => {
		let newItems = [...data.items];

		if (String(value).includes("c")) {
			newItems[i].unit_measurement = "cc";
		}
		if (String(value).includes("g")) {
			newItems[i].unit_measurement = "g";
		}
		if (String(value).includes("u")) {
			newItems[i].unit_measurement = "u";
		}

		if (newItems[i].unit_measurement === "u") {
			newItems[i].container_content = 1;
		}

		newItems[i].value_unit_measurement = value;
		setData({ ...data, items: newItems });
	};

	const changePrice = (i, value) => {
		value = String(value).replaceAll(".", "");

		let newItems = [...data.items];
		newItems[i].price_total = value;

		if (data && data.items[i]["quantity"] !== 0) {
			newItems[i]["price_unit"] = parseFloat(String(value).replaceAll(",", ".")) / data.items[i]["quantity"];
		}

		delayedQuery(newItems, i, data.type_currency_id);
		setData({ ...data, items: newItems });
	};

	const changeExpirationDate = (i, value) => {
		let newItems = [...data.items];
		newItems[i].expiration_date = value;
		setData({ ...data, items: newItems });
	};

	const changeTypeProduct = (i, value) => {
		let newItems = [...data.items];
		newItems[i].type_product = value;
		setData({ ...data, items: newItems });
	};

	const changeLotProduction = (i, value) => {
		let newItems = [...data.items];
		newItems[i].lot_production = value;
		setData({ ...data, items: newItems });
	};

	const changeProductGeneric = (i, opt, _meta) => {
		if (data && !data.estate_id) {
			addToast("Debe seleccionar un campo para ingresar un producto genérico.", {
				appearance: "error",
			});
			return;
		}

		let value = "";
		let newItems = [...data.items];
		let newProductCustomer = [...products_generic];

		if (opt && opt.value) {
			const productCustomerExist = products_generic.find((p) => p.value === opt.value);
			if (!productCustomerExist) {
				value = new Date().getTime() + "_" + opt.value;
				newProductCustomer.push({
					value: value,
					label: opt.value,
					estate_id: data.estate_id,
				});
				setProductsGeneric(newProductCustomer);
			} else {
				value = productCustomerExist.value;
			}
		}

		newItems[i].save_as = value;
		setData({ ...data, items: newItems, productCustomer: newProductCustomer });
		setReload(true);
	};

	const changeQuantityLot = (i, value) => {
		value = String(value).replaceAll(".", "");

		let newItems = [...data.items];
		newItems[i].quantity_lot = value;
		setData({ ...data, items: newItems });
	};

	const changeAlerts = () => {
		let newAlerts = [...alerts];
		newAlerts.forEach((element, index) => {
			newAlerts[index].completed = true;
		});
		setAlerts(newAlerts);
	};

	const activeAlerts = alerts.filter((al) => !al.completed);

	return (
		<Col md={12} className={classes.col_base}>
			<Card className={activeProduct && "active"}>
				<Card.Header onClick={() => setActiveProduct(!activeProduct)} className={activeProduct && "active"}>
					Productos
					<div>
						<span style={{ marginRight: "1em" }}>
							<strong> Cant. Envases:</strong> {formatMiles(quantityContainers)}
						</span>
						<span>
							<strong> Precio Total:</strong> ${formatMilesDecimalNew(net)}
						</span>
					</div>
				</Card.Header>

				{activeProduct && (
					<>
						<Card.Body>
							{activeAlerts.length !== 0 && (
								<CustomInfoMessage
									type="warning"
									title="Atención a los siguientes puntos"
									messages={[...activeAlerts.map((alert) => alert.text)]}
									onClose={changeAlerts}
								/>
							)}

							{/* <CreateOrderSaleTableHead /> */}
							<Row className={classes.body_table}>
								<Col md={12} sm={12}>
									{isFetchingDetail ? (
										<Fetching topMargin={"2em"} />
									) : (
										<CreateOrderSaleTableItem
											data={data}
											setData={setData}
											products={products}
											products_generic={products_generic}
											changeProduct={changeProduct}
											changeTextProduct={changeTextProduct}
											changeCas={changeCas}
											changeQuantity={changeQuantity}
											changeContainerContent={changeContainerContent}
											changeUnitMeasurement={changeUnitMeasurement}
											changePrice={changePrice}
											changeExpirationDate={changeExpirationDate}
											changeTypeProduct={changeTypeProduct}
											changeLotProduction={changeLotProduction}
											changeProductGeneric={changeProductGeneric}
											changeQuantityLot={changeQuantityLot}
										/>
									)}
								</Col>
							</Row>
						</Card.Body>
						<Card.Footer>
							<AddIcon className={classes.iconAdd} onClick={() => handleAddItem()} />
						</Card.Footer>
					</>
				)}
			</Card>
		</Col>
	);
};

export default CreateOrderSaleTableDetail;
